import { cn } from "@/lib/utils";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { ProjectedAttachment } from "../../lib/attachmentProjection";
import { firstLine, noteCardText, restLines } from "../../lib/noteContent";
import type { NotePreviewStyle } from "../../store/ui";
import { useUIStore } from "../../store/ui";
import type { Note } from "../../types";
import { Button } from "../ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { NoteAttachmentStrip } from "./NoteAttachmentStrip";
import { Icon } from "@/components/ui/Icon";
import { useAuthStore } from "../../store/auth";
import { isSharedByMe, isSharedWithMe, type NoteSharingFields } from "../../lib/sharing";
import { useSharingFeature } from "../../hooks/useSharingFeature";

/** A row shows its "active note" highlight only outside selection mode — while
 *  selecting, the open note must not light up so it isn't mistaken for selected. */
export function activeHighlightVisible(
  isActive: boolean,
  isSelectionMode: boolean,
): boolean {
  return isActive && !isSelectionMode;
}

interface NoteItemProps {
  note: Note & { sharedWithMe?: boolean; sharedAuthorName?: string } & NoteSharingFields;
  isActive?: boolean;
  isSelected?: boolean;
  isSelectionMode?: boolean;
  dateLabel: string;
  previewStyle?: NotePreviewStyle;
  attachments?: ProjectedAttachment[];
  /** The row above/below is also selected, so the shared corner flattens and
   *  consecutive selections merge into one block. */
  mergesTop?: boolean;
  mergesBottom?: boolean;
  /** Draw the hairline divider at this row's bottom edge (hidden around any
   *  highlight fill). */
  showSeparatorBelow?: boolean;
  dropdownContent?: React.ReactNode;
  onClick?: (e?: React.MouseEvent) => void;
  onTouchHandlers?: {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onTouchEnd: () => void;
    didLongPress: React.RefObject<boolean>;
    pressing?: boolean;
  };
}

export function NoteItem({
  note,
  isActive = false,
  isSelected = false,
  isSelectionMode = false,
  dateLabel,
  previewStyle = "large",
  attachments,
  mergesTop = false,
  mergesBottom = false,
  showSeparatorBelow = false,
  dropdownContent,
  onClick,
  onTouchHandlers,
}: NoteItemProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const card = useMemo(() => noteCardText(note.content), [note.content]);
  // A note that starts with body text shows no title row: its preview starts
  // at line 1 and takes the title's line. Density "small" has no preview, so
  // it always keeps the title.
  const showsTitle = previewStyle === "small" || card.showsTitle;
  const preview = card.preview;
  const activeTint = activeHighlightVisible(isActive, isSelectionMode) && !isSelected;
  const hasAttachments = previewStyle !== "small" && !!attachments?.length;
  const meId = useAuthStore((st) => st.user?.id);
  // Someone else's note shared with this account (a stub of the old public
  // link inbox, or a row of `notes` with sharing on), or one of ours shared.
  // The new badges and copy wait for `collabFeatures.sharing` (no new UI
  // before the beta); off, the row is what it was.
  const sharingOn = useSharingFeature();
  const withMe = !!note.sharedWithMe || (sharingOn && isSharedWithMe(note, meId));
  const byMe = sharingOn && !withMe && isSharedByMe(note, meId);
  const sharerName = note.sharedWithMe ? note.sharedAuthorName : withMe ? note.ownerName : undefined;

  const badges = (
    <div className="flex shrink-0 flex-row items-center gap-1.5">
      {withMe && (
        <span className="text-accent" title={t("sharedView.sharedWithYou")}>
          <Icon name="users" size={13} />
        </span>
      )}
      {byMe && (
        <span className="text-ink-secondary" title={t("sharing.sharedList")} data-shared-badge>
          <Icon name="users" size={13} />
        </span>
      )}
      {note.isPinned && (
        <span className="text-pin">
          <Icon name="pin" size={13} />
        </span>
      )}
      {note.isReadOnly && (
        <span className="text-ink-secondary" title={t("noteActions.readOnlyNote")}>
          <Icon name="read-only" size={13} />
        </span>
      )}
    </div>
  );

  const inner = (
    <>
      {/* Title row, with the badges at its right end like the native card. */}
      {showsTitle && (
        <div className="flex items-center gap-1.5 mb-1">
          <p
            className={cn(
              "truncate flex-1 min-w-0",
              "font-semibold leading-[1.3] tracking-tight",
              // The native list's sizes: 18 / 16 / 15 for large / medium / small.
              previewStyle === "small"
                ? "text-[15px]"
                : previewStyle === "large"
                  ? "text-[18px]"
                  : "text-[16px]",
              activeTint ? "text-accent-text" : "text-ink",
            )}
          >
            {note.title || t("noteList.untitled")}
          </p>
          {badges}
        </div>
      )}

      {/* Preview text + attachment strip */}
      {previewStyle !== "small" && (
        <>
          {preview ? (
            <p
              className={cn(
                "mb-1 text-muted leading-[1.35] whitespace-pre-line",
                // Native: two lines on medium, four on large.
                // Without a title row the preview gets its line: 3 and 5.
                previewStyle === "medium"
                  ? cn("text-[13px]", showsTitle ? "line-clamp-2" : "line-clamp-3")
                  : cn("text-[14px]", showsTitle ? "line-clamp-4" : "line-clamp-5"),
              )}
              style={{
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
              }}
            >
              {showsTitle ? (
                preview
              ) : (
                // Line 1 stands in for the title: its colour, active tint included.
                <>
                  <span className={activeTint ? "text-accent-text" : "text-ink"}>{firstLine(preview)}</span>
                  {restLines(preview)}
                </>
              )}
            </p>
          ) : (
            // Attachment-only notes show their thumbnails/chips instead of the
            // "No content" placeholder.
            !hasAttachments && (
              <p className="text-[13px] leading-[1.35] mb-1 text-placeholder">
                {t("noteList.noContent")}
              </p>
            )
          )}
          {hasAttachments && (
            <NoteAttachmentStrip attachments={attachments!} />
          )}
        </>
      )}

      {/* Date row, with the badges when there is no title row. */}
      <div className="flex items-center gap-1.5">
        <p className="text-[12px] leading-4 text-muted truncate flex-1 min-w-0">
          {dateLabel}
          {withMe && sharerName && (
            <> · {sharingOn ? t("sharing.sharedBy", { name: sharerName }) : sharerName}</>
          )}
        </p>
        {!showsTitle && badges}
      </div>
    </>
  );

  return (
    <div
      ref={ref}
      className={cn(
        // Flat list rows (matching the native app): transparent on the
        // `bg-notelist` column, packed edge-to-edge and divided by a hairline.
        // Active / selected fills a rounded `bg-active`; consecutive selections
        // merge by flattening the shared corner (`rounded-t/b-none`).
        "group relative transition-all duration-150 touch-manipulation select-none [-webkit-user-select:none]",
        onClick && "cursor-pointer",
        isSelected || activeHighlightVisible(isActive, isSelectionMode)
          ? cn(
              "bg-active rounded-2xl",
              mergesTop && "rounded-t-none",
              mergesBottom && "rounded-b-none",
            )
          : onTouchHandlers?.pressing && "bg-hover",
      )}
    >
      {/* Hairline divider between rows. Inset to line up with the row text;
          hidden around any highlight fill by the list (`showSeparatorBelow`). */}
      {/* Phones only: the Mac list has no separators, iOS has hairlines. */}
      {showSeparatorBelow && (
        <div className="pointer-events-none absolute inset-x-3 bottom-0 h-px bg-border md:hidden" />
      )}
      {onClick ? (
        <button
          onClick={(e) => {
            // If a long-press just fired, don't navigate
            if (onTouchHandlers?.didLongPress.current) {
              (
                onTouchHandlers.didLongPress as React.MutableRefObject<boolean>
              ).current = false;
              return;
            }
            onClick(e);
          }}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !isSelectionMode) {
              e.preventDefault();
              useUIStore.getState().requestEditorFocus();
            }
          }}
          onTouchStart={onTouchHandlers?.onTouchStart}
          onTouchMove={onTouchHandlers?.onTouchMove}
          onTouchEnd={onTouchHandlers?.onTouchEnd}
          data-note-id={note.id}
          className="w-full text-left px-3 py-3 rounded-[inherit] touch-manipulation"
        >
          {inner}
        </button>
      ) : (
        <div className="w-full text-left px-3 py-3">{inner}</div>
      )}

      {/* Hover ellipsis dropdown (desktop only) */}
      {dropdownContent && (
        <div className="absolute right-2 top-2 opacity-0 transition-opacity [@media(hover:hover)]:group-hover:opacity-100 focus-within:opacity-100">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7 rounded-full shadow-sm !text-muted hover:!text-ink hover:!bg-border"
                onClick={(e) => e.stopPropagation()}
              >
                <Icon name="more" size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              side="bottom"
              className="min-w-[160px]"
            >
              {dropdownContent}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}
